import { Button, Group, Stack, Text } from '@mantine/core';
import styled from 'styled-components';
import { PALETTE } from '@dealstack/shared/lib/common/business/stampDesign';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import type { SlotListItem } from './SlotList.types.ts';

interface Props {
	slots: SlotListItem[];
	selectedSlotKey: string | null;
	onSelect: (key: string) => void;
	onLocate: (key: string) => void;
	onPlace: (key: string) => void;
}

const BRAND = PALETTE.brand;

const Row = styled(Group)<{ $active: boolean }>`
	width: 100%;
	padding: 0.5rem 0.75rem;
	border-radius: 4px;
	cursor: pointer;
	background-color: ${({ $active, theme }) =>
		$active ? theme.v3.colors.green[1] : 'transparent'};

	&:hover {
		background-color: ${({ theme }) => theme.v3.colors.gray[1]};
	}
`;

const Dot = styled.span<{ $filled: boolean }>`
	display: inline-block;
	width: 10px;
	height: 10px;
	border-radius: 50%;
	background-color: ${({ $filled }) => ($filled ? BRAND : 'transparent')};
	border: 1.5px solid ${({ $filled, theme }) => ($filled ? BRAND : theme.v3.colors.gray[5])};
	flex-shrink: 0;
`;

export const SlotList = ({ slots, selectedSlotKey, onSelect, onLocate, onPlace }: Props) => {
	return (
		<Stack gap="xs" data-testid={TEST_ELEMENT_IDS.PLACEMENT_EDITOR_SLOT_LIST}>
			{slots.map((slot) => {
				const active = selectedSlotKey === slot.key;
				return (
					<Row
						key={slot.key}
						$active={active}
						justify="space-between"
						wrap="nowrap"
						onClick={() => onSelect(slot.key)}
						data-testid={`${TEST_ELEMENT_IDS.PLACEMENT_EDITOR_SLOT_ROW}-${slot.key}`}
					>
						<Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
							<Dot $filled={slot.isPlaced} />
							<Text size="sm" truncate>
								{slot.label}
							</Text>
						</Group>
						{slot.isPlaced ? (
							<Button
								variant="subtle"
								size="xs"
								onClick={(e) => {
									e.stopPropagation();
									onLocate(slot.key);
								}}
							>
								Locate
							</Button>
						) : (
							<Button
								variant="filled"
								size="xs"
								onClick={(e) => {
									e.stopPropagation();
									onPlace(slot.key);
								}}
							>
								Place
							</Button>
						)}
					</Row>
				);
			})}
		</Stack>
	);
};
